import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Video, Plus, ArrowRight, Users, Phone, PhoneOff, ExternalLink,
  Copy, Check, X, Clock, Calendar, Bell, Play, Square, Search,
  UserPlus, Globe, User, AlertCircle, CheckCircle, RefreshCw
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VideoMeetings() {
  const navigate = useNavigate();
  const { t, isRTL, dir } = useLanguage();
  const jitsiContainerRef = useRef(null);
  const jitsiApiRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [activeMeetings, setActiveMeetings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showVideoCall, setShowVideoCall] = useState(false);
  const [currentMeeting, setCurrentMeeting] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('active'); // active, scheduled, history
  const [notifications, setNotifications] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    meeting_type: 'internal',
    meeting_mode: 'video',
    participants: [],
    participant_names: [],
    scheduled_time: '',
    duration_minutes: 60,
    notes: '',
    is_instant: false,
    is_all_employees: false
  });

  // جلب البيانات
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [meetingsRes, activeRes, employeesRes, notifRes] = await Promise.all([
        axios.get(`${API}/meetings`, { headers }),
        axios.get(`${API}/meetings/active`, { headers }),
        axios.get(`${API}/employees/list`, { headers }),
        axios.get(`${API}/notifications`, { headers }).catch(() => ({ data: { notifications: [] } }))
      ]);
      
      setMeetings(meetingsRes.data || []);
      setActiveMeetings(activeRes.data.meetings || []);
      setEmployees(employeesRes.data.employees || []);
      
      // فلترة إشعارات الاجتماعات
      const meetingNotifs = (notifRes.data.notifications || []).filter(
        n => n.type?.includes('meeting') && !n.is_read
      );
      setNotifications(meetingNotifs);
      
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      setUser(JSON.parse(userStr));
    }
    fetchData();
    
    // تحديث الاجتماعات كل 10 ثواني
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // تحميل Jitsi Meet API
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://meet.jit.si/external_api.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      if (jitsiApiRef.current) {
        jitsiApiRef.current.dispose();
      }
    };
  }, []);

  // إنشاء اجتماع فوري
  const createInstantMeeting = async (isForAll = false) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/meetings/instant`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          title: 'اجتماع فوري',
          is_all_employees: isForAll,
          participant_ids: isForAll ? [] : formData.participants
        }
      });
      
      setSuccess('تم إنشاء الاجتماع الفوري');
      setCurrentMeeting(response.data.meeting);
      startJitsiCall(response.data.meeting);
      fetchData();
      setShowModal(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل في إنشاء الاجتماع');
    }
  };

  // إنشاء اجتماع مجدول
  const createScheduledMeeting = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      const meetingData = {
        ...formData,
        is_instant: false
      };
      
      await axios.post(`${API}/meetings`, meetingData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      setSuccess('تم جدولة الاجتماع بنجاح');
      fetchData();
      setShowModal(false);
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'فشل في إنشاء الاجتماع');
    }
  };

  // بدء مكالمة Jitsi
  const startJitsiCall = (meeting) => {
    setCurrentMeeting(meeting);
    setShowVideoCall(true);
    
    setTimeout(() => {
      if (jitsiContainerRef.current && window.JitsiMeetExternalAPI) {
        if (jitsiApiRef.current) {
          jitsiApiRef.current.dispose();
        }
        
        const roomName = meeting.jitsi_room_id || `hklaw-${meeting.id.substring(0, 8)}`;
        
        jitsiApiRef.current = new window.JitsiMeetExternalAPI('meet.jit.si', {
          roomName: roomName,
          parentNode: jitsiContainerRef.current,
          userInfo: {
            displayName: user?.full_name || 'مستخدم'
          },
          configOverwrite: {
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            defaultLanguage: 'ar'
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            SHOW_WATERMARK_FOR_GUESTS: false,
            DEFAULT_BACKGROUND: '#1a365d',
            TOOLBAR_BUTTONS: [
              'microphone', 'camera', 'desktop', 'fullscreen',
              'fodeviceselection', 'hangup', 'chat', 'raisehand',
              'participants-pane', 'tileview', 'settings'
            ]
          }
        });

        jitsiApiRef.current.addListener('videoConferenceLeft', () => {
          endCall();
        });
      }
    }, 100);
  };

  // إنهاء المكالمة
  const endCall = async () => {
    if (jitsiApiRef.current) {
      jitsiApiRef.current.dispose();
      jitsiApiRef.current = null;
    }
    
    if (currentMeeting) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/meetings/${currentMeeting.id}/end`, null, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error ending meeting:', err);
      }
    }
    
    setShowVideoCall(false);
    setCurrentMeeting(null);
    fetchData();
  };

  // الانضمام لاجتماع
  const joinMeeting = async (meeting) => {
    if (meeting.status !== 'active') {
      // بدء الاجتماع أولاً
      try {
        const token = localStorage.getItem('token');
        await axios.put(`${API}/meetings/${meeting.id}/start`, null, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (err) {
        console.error('Error starting meeting:', err);
      }
    }
    startJitsiCall(meeting);
  };

  // نسخ رابط الاجتماع
  const copyMeetingLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // إضافة/إزالة مشارك
  const toggleParticipant = (emp) => {
    const isSelected = formData.participants.includes(emp.id);
    if (isSelected) {
      setFormData({
        ...formData,
        participants: formData.participants.filter(id => id !== emp.id),
        participant_names: formData.participant_names.filter(name => name !== emp.full_name)
      });
    } else {
      setFormData({
        ...formData,
        participants: [...formData.participants, emp.id],
        participant_names: [...formData.participant_names, emp.full_name]
      });
    }
  };

  // إعادة تعيين النموذج
  const resetForm = () => {
    setFormData({
      title: '',
      meeting_type: 'internal',
      meeting_mode: 'video',
      participants: [],
      participant_names: [],
      scheduled_time: '',
      duration_minutes: 60,
      notes: '',
      is_instant: false,
      is_all_employees: false
    });
  };

  // تصنيف الاجتماعات
  const scheduledMeetings = meetings.filter(m => m.status === 'scheduled');
  const historyMeetings = meetings.filter(m => m.status === 'ended' || m.status === 'cancelled');

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" dir={dir}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gold mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('msg.loading') || 'جاري التحميل...'}</p>
        </div>
      </div>
    );
  }

  // عرض مكالمة الفيديو
  if (showVideoCall) {
    return (
      <div className="fixed inset-0 bg-gray-900 z-50 flex flex-col">
        {/* شريط المعلومات */}
        <div className="bg-primary text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Video className="w-6 h-6" />
            <div>
              <h2 className="font-bold">{currentMeeting?.title || 'اجتماع فيديو'}</h2>
              <p className="text-sm text-white/70">
                {currentMeeting?.participant_names?.join(', ')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => copyMeetingLink(currentMeeting?.meeting_link)}
              className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg hover:bg-white/20"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              <span className="text-sm">{copied ? 'تم النسخ' : 'نسخ الرابط'}</span>
            </button>
            <button
              onClick={endCall}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 rounded-lg hover:bg-red-700"
            >
              <PhoneOff className="w-5 h-5" />
              <span>إنهاء المكالمة</span>
            </button>
          </div>
        </div>
        
        {/* نافذة Jitsi */}
        <div ref={jitsiContainerRef} className="flex-1" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand" dir={dir}>
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            >
              <ArrowRight className="w-5 h-5" />
              {t('btn.back') || 'رجوع'}
            </button>
            <h1 className="text-xl font-heading font-bold flex items-center gap-2">
              <Video className="w-6 h-6" />
              {t('meetings.videoMeetings') || 'اجتماعات الفيديو'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {/* إشعارات الاجتماعات */}
            {notifications.length > 0 && (
              <div className="relative">
                <Bell className="w-6 h-6 text-yellow-400 animate-pulse" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {notifications.length}
                </span>
              </div>
            )}
            <LanguageSelector variant="minimal" className="text-white" />
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        {/* رسائل */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700">{success}</span>
          </div>
        )}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="text-red-700">{error}</span>
            <button onClick={() => setError('')} className="mr-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* إشعارات الاجتماعات الفورية */}
        {notifications.length > 0 && (
          <div className="mb-6 space-y-2">
            {notifications.map(notif => (
              <div
                key={notif.id}
                className="p-4 bg-yellow-50 border-2 border-yellow-400 rounded-xl flex items-center justify-between animate-pulse"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center animate-bounce">
                    <Video className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-red-600">{notif.title}</h3>
                    <p className="text-gray-600">{notif.message}</p>
                  </div>
                </div>
                {notif.meeting_link && (
                  <button
                    onClick={() => {
                      const meeting = meetings.find(m => m.id === notif.related_id);
                      if (meeting) joinMeeting(meeting);
                      else window.open(notif.meeting_link, '_blank');
                    }}
                    className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 flex items-center gap-2 font-bold"
                  >
                    <Phone className="w-5 h-5" />
                    انضم الآن
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* أزرار الإجراءات السريعة */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* اجتماع فوري للجميع */}
          <button
            onClick={() => createInstantMeeting(true)}
            className="p-6 bg-gradient-to-br from-red-500 to-red-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all group"
            data-testid="instant-meeting-all-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Globe className="w-8 h-8" />
              </div>
              <div className="text-right">
                <h3 className="text-xl font-bold">{t('meetings.instantMeetingAll') || 'اجتماع فوري للجميع'}</h3>
                <p className="text-white/80 text-sm">{t('meetings.callAllEmployees') || 'دعوة جميع الموظفين الآن'}</p>
              </div>
            </div>
          </button>

          {/* اجتماع فوري مع مشاركين */}
          <button
            onClick={() => {
              setFormData({ ...formData, is_instant: true, is_all_employees: false });
              setShowModal(true);
            }}
            className="p-6 bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all group"
            data-testid="instant-meeting-select-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <UserPlus className="w-8 h-8" />
              </div>
              <div className="text-right">
                <h3 className="text-xl font-bold">{t('meetings.instantMeetingSelect') || 'اجتماع فوري'}</h3>
                <p className="text-white/80 text-sm">{t('meetings.selectParticipants') || 'اختر المشاركين'}</p>
              </div>
            </div>
          </button>

          {/* جدولة اجتماع */}
          <button
            onClick={() => {
              setFormData({ ...formData, is_instant: false });
              setShowModal(true);
            }}
            className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all group"
            data-testid="schedule-meeting-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Calendar className="w-8 h-8" />
              </div>
              <div className="text-right">
                <h3 className="text-xl font-bold">{t('meetings.scheduleMeeting') || 'جدولة اجتماع'}</h3>
                <p className="text-white/80 text-sm">{t('meetings.planFutureMeeting') || 'تحديد موعد لاحق'}</p>
              </div>
            </div>
          </button>
        </div>

        {/* التبويبات */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="flex border-b">
            <button
              onClick={() => setActiveTab('active')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'active'
                  ? 'bg-green-50 text-green-600 border-b-2 border-green-500'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <div className={`w-3 h-3 rounded-full ${activeMeetings.length > 0 ? 'bg-green-500 animate-pulse' : 'bg-gray-300'}`} />
                {t('meetings.activeMeetings') || 'اجتماعات نشطة'} ({activeMeetings.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('scheduled')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'scheduled'
                  ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Clock className="w-5 h-5" />
                {t('meetings.scheduledMeetings') || 'اجتماعات مجدولة'} ({scheduledMeetings.length})
              </div>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                activeTab === 'history'
                  ? 'bg-gray-100 text-gray-600 border-b-2 border-gray-500'
                  : 'text-gray-500 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Calendar className="w-5 h-5" />
                {t('meetings.history') || 'السجل'} ({historyMeetings.length})
              </div>
            </button>
          </div>

          <div className="p-6">
            {/* الاجتماعات النشطة */}
            {activeTab === 'active' && (
              <div className="space-y-4">
                {activeMeetings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Video className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">{t('meetings.noActiveMeetings') || 'لا توجد اجتماعات نشطة حالياً'}</p>
                    <p className="text-sm">{t('meetings.startNewMeeting') || 'ابدأ اجتماعاً جديداً من الأزرار أعلاه'}</p>
                  </div>
                ) : (
                  activeMeetings.map(meeting => (
                    <div
                      key={meeting.id}
                      className="p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center justify-between"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-green-500 rounded-xl flex items-center justify-center animate-pulse">
                          <Video className="w-7 h-7 text-white" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-primary">{meeting.title}</h3>
                          <p className="text-sm text-gray-600">
                            <Users className="w-4 h-4 inline ml-1" />
                            {meeting.participant_names?.join(', ')}
                          </p>
                          <p className="text-xs text-green-600 mt-1">
                            🟢 جاري الآن
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => copyMeetingLink(meeting.meeting_link)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                          title="نسخ الرابط"
                        >
                          {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5" />}
                        </button>
                        <button
                          onClick={() => joinMeeting(meeting)}
                          className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 flex items-center gap-2 font-bold"
                        >
                          <Phone className="w-5 h-5" />
                          {t('meetings.join') || 'انضم'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* الاجتماعات المجدولة */}
            {activeTab === 'scheduled' && (
              <div className="space-y-4">
                {scheduledMeetings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">{t('meetings.noScheduledMeetings') || 'لا توجد اجتماعات مجدولة'}</p>
                  </div>
                ) : (
                  scheduledMeetings.map(meeting => (
                    <div
                      key={meeting.id}
                      className="p-4 bg-white border rounded-xl flex items-center justify-between hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                          <Calendar className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-primary">{meeting.title}</h3>
                          <p className="text-sm text-gray-600">
                            <Users className="w-4 h-4 inline ml-1" />
                            {meeting.participant_names?.join(', ')}
                          </p>
                          <p className="text-xs text-blue-600 mt-1">
                            <Clock className="w-3 h-3 inline ml-1" />
                            {new Date(meeting.scheduled_time).toLocaleString('ar-SA')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => copyMeetingLink(meeting.meeting_link)}
                          className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                          title="نسخ الرابط"
                        >
                          <Copy className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => joinMeeting(meeting)}
                          className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 flex items-center gap-2"
                        >
                          <Play className="w-5 h-5" />
                          {t('meetings.start') || 'ابدأ'}
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* السجل */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                {historyMeetings.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Clock className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg">{t('meetings.noHistory') || 'لا يوجد سجل اجتماعات'}</p>
                  </div>
                ) : (
                  historyMeetings.map(meeting => (
                    <div
                      key={meeting.id}
                      className="p-4 bg-gray-50 border rounded-xl flex items-center justify-between opacity-75"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-gray-200 rounded-xl flex items-center justify-center">
                          <Video className="w-7 h-7 text-gray-500" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-600">{meeting.title}</h3>
                          <p className="text-sm text-gray-500">
                            {meeting.participant_names?.join(', ')}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(meeting.scheduled_time).toLocaleString('ar-SA')}
                          </p>
                        </div>
                      </div>
                      <span className="px-3 py-1 bg-gray-200 text-gray-600 rounded-full text-sm">
                        {meeting.status === 'ended' ? 'منتهي' : 'ملغي'}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* نافذة إنشاء اجتماع */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex items-center justify-between bg-gray-50">
              <h3 className="font-bold text-lg text-primary flex items-center gap-2">
                {formData.is_instant ? (
                  <>
                    <Phone className="w-5 h-5" />
                    {t('meetings.instantMeeting') || 'اجتماع فوري'}
                  </>
                ) : (
                  <>
                    <Calendar className="w-5 h-5" />
                    {t('meetings.scheduleMeeting') || 'جدولة اجتماع'}
                  </>
                )}
              </h3>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 hover:bg-gray-200 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={formData.is_instant ? (e) => { e.preventDefault(); createInstantMeeting(false); } : createScheduledMeeting} className="p-6 space-y-6">
              {/* عنوان الاجتماع */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('meetings.title') || 'عنوان الاجتماع'}
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                  placeholder={formData.is_instant ? 'اجتماع فوري' : 'أدخل عنوان الاجتماع'}
                  required={!formData.is_instant}
                />
              </div>

              {/* للجميع أم مشاركين محددين */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {t('meetings.participants') || 'المشاركون'}
                </label>
                <div className="flex gap-4 mb-4">
                  <label className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.is_all_employees
                      ? 'border-gold bg-gold/10 text-gold'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="participant_type"
                      checked={formData.is_all_employees}
                      onChange={() => setFormData({ ...formData, is_all_employees: true, participants: [], participant_names: [] })}
                      className="sr-only"
                    />
                    <Globe className="w-6 h-6" />
                    <span className="font-semibold">{t('meetings.allEmployees') || 'جميع الموظفين'}</span>
                  </label>
                  <label className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    !formData.is_all_employees
                      ? 'border-gold bg-gold/10 text-gold'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="radio"
                      name="participant_type"
                      checked={!formData.is_all_employees}
                      onChange={() => setFormData({ ...formData, is_all_employees: false })}
                      className="sr-only"
                    />
                    <UserPlus className="w-6 h-6" />
                    <span className="font-semibold">{t('meetings.selectParticipants') || 'اختيار المشاركين'}</span>
                  </label>
                </div>

                {/* قائمة الموظفين */}
                {!formData.is_all_employees && (
                  <div className="border rounded-lg p-4 max-h-48 overflow-y-auto space-y-2">
                    {employees.map(emp => (
                      <label
                        key={emp.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                          formData.participants.includes(emp.id)
                            ? 'bg-gold/10 border border-gold'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.participants.includes(emp.id)}
                          onChange={() => toggleParticipant(emp)}
                          className="w-5 h-5 rounded border-gray-300 text-gold focus:ring-gold"
                        />
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{emp.full_name}</p>
                          <p className="text-xs text-gray-500">{emp.role}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* موعد الاجتماع (للمجدول فقط) */}
              {!formData.is_instant && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('meetings.dateTime') || 'التاريخ والوقت'}
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_time}
                      onChange={(e) => setFormData({ ...formData, scheduled_time: e.target.value })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('meetings.duration') || 'المدة (دقيقة)'}
                    </label>
                    <select
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) })}
                      className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                    >
                      <option value={15}>15 دقيقة</option>
                      <option value={30}>30 دقيقة</option>
                      <option value={45}>45 دقيقة</option>
                      <option value={60}>ساعة</option>
                      <option value={90}>ساعة ونصف</option>
                      <option value={120}>ساعتين</option>
                    </select>
                  </div>
                </div>
              )}

              {/* ملاحظات */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('meetings.notes') || 'ملاحظات'}
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-gold outline-none"
                  rows={3}
                  placeholder={t('meetings.notesPlaceholder') || 'أضف ملاحظات أو جدول الأعمال...'}
                />
              </div>

              {/* أزرار */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="flex-1 py-3 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  {t('btn.cancel') || 'إلغاء'}
                </button>
                <button
                  type="submit"
                  className={`flex-1 py-3 text-white rounded-lg flex items-center justify-center gap-2 ${
                    formData.is_instant
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-gold hover:bg-gold-dark'
                  }`}
                >
                  {formData.is_instant ? (
                    <>
                      <Phone className="w-5 h-5" />
                      {t('meetings.startNow') || 'ابدأ الآن'}
                    </>
                  ) : (
                    <>
                      <Calendar className="w-5 h-5" />
                      {t('meetings.schedule') || 'جدولة'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
